import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { generatePageNumbers, PAGE_SIZE_OPTIONS, type PaginationMetadata } from '@/types/pagination';

interface PaginationControlsProps {
  pagination: PaginationMetadata;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  className?: string;
}

export function PaginationControls({
  pagination,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  className = '',
}: PaginationControlsProps) {
  const { currentPage, totalPages, totalItems, hasNextPage, hasPreviousPage, pageSize } = pagination;

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>

      {/* Left: per-page selector */}
      {showPageSizeSelector && onPageSizeChange ? (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-7 w-[58px] text-xs px-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div />
      )}

      {/* Centre: compact page info */}
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        Page {currentPage} of {totalPages}
      </span>

      {/* Right: prev / page numbers / next */}
      <Pagination className="mx-0 w-auto">
        <PaginationContent className="gap-0.5">
          <PaginationItem>
            <PaginationPrevious
              onClick={hasPreviousPage ? () => onPageChange(currentPage - 1) : undefined}
              aria-disabled={!hasPreviousPage}
              className={`h-7 text-xs px-2 [&>span]:hidden ${!hasPreviousPage ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}
            />
          </PaginationItem>

          {pageNumbers.map((pageNum, index) => (
            <PaginationItem key={`${pageNum}-${index}`}>
              {pageNum === 'ellipsis' ? (
                <PaginationEllipsis className="size-7" />
              ) : (
                <PaginationLink
                  onClick={() => onPageChange(pageNum)}
                  isActive={pageNum === currentPage}
                  className="h-7 w-7 text-xs cursor-pointer"
                >
                  {pageNum}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              onClick={hasNextPage ? () => onPageChange(currentPage + 1) : undefined}
              aria-disabled={!hasNextPage}
              className={`h-7 text-xs px-2 [&>span]:hidden ${!hasNextPage ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
